if (!customElements.get('product-features')) {
  class ProductFeatures extends HTMLElement {
    constructor() {
      super();
      this.modal = this.querySelector('[data-feature-modal]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.triggers = Array.from(this.querySelectorAll('[data-feature-trigger]'));
      this.selectors = Array.from(this.querySelectorAll('[data-feature-select]'));
      this.panels = Array.from(this.querySelectorAll('[data-feature-panel]'));
      this.closeButtons = Array.from(this.querySelectorAll('[data-feature-close]'));
      this.activeTrigger = null;

      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.modal || !this.dialog) return;
      
      document.body.appendChild(this.modal);

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.activeTrigger = trigger;
          this.showFeature(trigger.dataset.featureTrigger);
          this.open();
        });
      });

      this.selectors.forEach((selector) => {
        selector.addEventListener('click', () => {
          this.showFeature(selector.dataset.featureSelect);
        });
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
    }

    open() {
      this.modal.hidden = false;
      document.body.classList.add('product-features-open');
      document.addEventListener('keydown', this.handleKeydown);
      window.requestAnimationFrame(() => this.dialog.focus());
    }

    close() {
      if (this.modal.hidden) return;

      this.modal.hidden = true;
      document.body.classList.remove('product-features-open');
      document.removeEventListener('keydown', this.handleKeydown);

      if (this.activeTrigger) {
        this.activeTrigger.focus();
      }
    }

    showFeature(index) {
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.featurePanel === index;
        panel.hidden = !isActive;
      });

      this.selectors.forEach((selector) => {
        const isActive = selector.dataset.featureSelect === index;
        selector.classList.toggle('is-active', isActive);
        selector.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) {
          selector.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('product-features', ProductFeatures);
}

class ProductFeaturesGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.track = gallery.querySelector('[data-product-features-gallery-track]');
    this.slides = Array.from(gallery.querySelectorAll('[data-product-features-gallery-slide]'));
    this.prevButton = gallery.querySelector('[data-product-features-gallery-prev]');
    this.nextButton = gallery.querySelector('[data-product-features-gallery-next]');
    this.dots = Array.from(gallery.querySelectorAll('[data-product-features-gallery-dot]'));
    this.index = 0;
    this.interval = null;
    this.startX = 0;
    this.currentX = 0;
    this.dragOffset = 0;
    this.isPointerDown = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.track || this.slides.length < 2) return;

    this.bindEvents();
    this.update();
    this.startAutoplay();
  }

  bindEvents() {
    this.prevButton && this.prevButton.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.prevButton && this.prevButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.pauseAutoplay();
      this.goTo(this.index - 1);
    });

    this.nextButton && this.nextButton.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.nextButton && this.nextButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.pauseAutoplay();
      this.goTo(this.index + 1);
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('pointerdown', (event) => event.stopPropagation());
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.pauseAutoplay();
        this.goTo(Number(dot.dataset.productFeaturesGalleryDot));
      });
    });

    this.gallery.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.gallery.addEventListener('mouseleave', () => this.startAutoplay());
    this.gallery.addEventListener('focusin', () => this.pauseAutoplay());
    this.gallery.addEventListener('focusout', () => this.startAutoplay());

    this.gallery.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
    this.gallery.addEventListener('pointermove', (event) => this.handlePointerMove(event));
    this.gallery.addEventListener('pointerup', () => this.handlePointerUp());
    this.gallery.addEventListener('pointercancel', () => this.handlePointerUp());
  }

  startAutoplay() {
    if (this.prefersReducedMotion || this.interval || this.slides.length < 2) return;

    this.interval = window.setInterval(() => {
      this.goTo(this.index + 1);
    }, 3000);
  }

  pauseAutoplay() {
    if (!this.interval) return;

    window.clearInterval(this.interval);
    this.interval = null;
  }

  goTo(index) {
    if (index < 0) {
      this.index = this.slides.length - 1;
    } else if (index >= this.slides.length) {
      this.index = 0;
    } else {
      this.index = index;
    }

    this.update();
  }

  update() {
    this.track.style.transform = `translate3d(calc(-${this.index * 100}% + ${this.dragOffset}px), 0, 0)`;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  handlePointerDown(event) {
    if (event.target.closest('button')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pauseAutoplay();
    this.isPointerDown = true;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.dragOffset = 0;
    this.gallery.classList.add('is-dragging');
    this.gallery.setPointerCapture && this.gallery.setPointerCapture(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.isPointerDown) return;

    this.currentX = event.clientX;
    this.dragOffset = this.currentX - this.startX;
    this.update();
  }

  handlePointerUp() {
    if (!this.isPointerDown) return;

    const deltaX = this.currentX - this.startX;
    this.isPointerDown = false;
    this.dragOffset = 0;
    this.gallery.classList.remove('is-dragging');

    if (Math.abs(deltaX) > 40) {
      this.goTo(deltaX < 0 ? this.index + 1 : this.index - 1);
    } else {
      this.update();
    }

    this.startAutoplay();
  }
}

const initProductFeaturesGalleries = () => {
  document.querySelectorAll('[data-product-features-gallery]').forEach((gallery) => {
    if (gallery.dataset.productFeaturesGalleryInitialized === 'true') return;

    gallery.dataset.productFeaturesGalleryInitialized = 'true';
    new ProductFeaturesGallery(gallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductFeaturesGalleries);
} else {
  initProductFeaturesGalleries();
}
